import { useEffect, useRef, useState } from 'react';

const MIN_THUMB = 40;
const FADE_MS = 900;

export function CustomScrollbar() {
  const thumbRef = useRef<HTMLDivElement | null>(null);
  const fadeTimer = useRef<number | null>(null);
  const draggingRef = useRef(false);
  const dragOffset = useRef(0);
  const [active, setActive] = useState(false);
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    const updateThumb = () => {
      const thumb = thumbRef.current;
      if (!thumb) return;
      const docH = document.documentElement.scrollHeight;
      const winH = window.innerHeight;
      if (docH <= winH) {
        thumb.style.opacity = '0';
        return;
      }
      const ratio = winH / docH;
      const trackH = winH - 16; // padding vertical
      const thumbH = Math.max(MIN_THUMB, trackH * ratio);
      const maxThumbTop = trackH - thumbH;
      const scrollRatio = window.scrollY / (docH - winH);
      const top = 8 + maxThumbTop * scrollRatio;
      thumb.style.height = `${thumbH}px`;
      thumb.style.transform = `translateY(${top}px)`;
    };

    const showThumb = () => {
      setActive(true);
      if (fadeTimer.current) window.clearTimeout(fadeTimer.current);
      fadeTimer.current = window.setTimeout(() => {
        if (!draggingRef.current) setActive(false);
      }, FADE_MS);
    };

    const onScroll = () => {
      updateThumb();
      showThumb();
    };

    const onResize = () => updateThumb();

    updateThumb();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    // Observa cambios de altura del documento (imágenes, fuentes, etc.)
    // jsdom no implementa ResizeObserver — guard para entornos de test.
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateThumb) : null;
    ro?.observe(document.documentElement);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      ro?.disconnect();
      if (fadeTimer.current) window.clearTimeout(fadeTimer.current);
    };
  }, []);

  // Drag para arrastrar el thumb
  useEffect(() => {
    const thumb = thumbRef.current;
    if (!thumb) return;

    const onPointerDown = (e: PointerEvent) => {
      draggingRef.current = true;
      setActive(true);
      const rect = thumb.getBoundingClientRect();
      dragOffset.current = e.clientY - rect.top;
      thumb.setPointerCapture(e.pointerId);
      e.preventDefault();
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!draggingRef.current) return;
      const docH = document.documentElement.scrollHeight;
      const winH = window.innerHeight;
      const trackH = winH - 16;
      const thumbH = thumb.getBoundingClientRect().height;
      const newTop = e.clientY - dragOffset.current - 8;
      const ratio = Math.min(1, Math.max(0, newTop / (trackH - thumbH)));
      window.scrollTo({ top: ratio * (docH - winH), behavior: 'auto' });
    };
    const onPointerUp = (e: PointerEvent) => {
      draggingRef.current = false;
      thumb.releasePointerCapture(e.pointerId);
      setActive(true);
      if (fadeTimer.current) window.clearTimeout(fadeTimer.current);
      fadeTimer.current = window.setTimeout(() => setActive(false), FADE_MS);
    };

    thumb.addEventListener('pointerdown', onPointerDown);
    thumb.addEventListener('pointermove', onPointerMove);
    thumb.addEventListener('pointerup', onPointerUp);
    thumb.addEventListener('pointercancel', onPointerUp);

    return () => {
      thumb.removeEventListener('pointerdown', onPointerDown);
      thumb.removeEventListener('pointermove', onPointerMove);
      thumb.removeEventListener('pointerup', onPointerUp);
      thumb.removeEventListener('pointercancel', onPointerUp);
    };
  }, []);

  const visible = active || hovering;

  return (
    <div
      aria-hidden
      className="fixed top-0 right-1 z-[60] pointer-events-none"
      style={{ width: 8, height: '100vh' }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <div
        ref={thumbRef}
        className="absolute right-0 w-1.5 rounded-full pointer-events-auto cursor-grab active:cursor-grabbing"
        style={{
          background: visible
            ? 'linear-gradient(180deg, #FFD870, #FFC836 50%, #E6AC18)'
            : 'rgba(255,200,54,0.55)',
          boxShadow: visible
            ? '0 0 12px rgba(255,200,54,0.55), 0 0 2px rgba(255,200,54,0.9)'
            : '0 0 6px rgba(255,200,54,0.3)',
          opacity: visible ? 1 : 0.55,
          transition: 'opacity 350ms ease, background 250ms ease, box-shadow 250ms ease, width 200ms ease',
          width: visible ? 6 : 4,
          top: 0,
        }}
      />
    </div>
  );
}
